import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useContent, useWa } from "@/lib/content";
import { scrollToId } from "@/lib/smooth";
import { LOGO_MASK, WhatsIcon } from "./Primitives";

export const Nav = () => {
  const { NAV, TEXTS: T } = useContent();
  const waLink = useWa();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled ? "border-b border-gold/15 bg-espresso/80 py-3 backdrop-blur-xl" : "border-b border-transparent py-6"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 md:px-10">
        <button data-testid="nav-logo" onClick={() => go("inicio")} className="group flex items-center" aria-label="Café Amadeus - Inicio">
          <span className="relative block overflow-hidden">
            <motion.img
              src="/img/logo-amadeus-nav.png"
              alt="Café Amadeus"
              initial={{ clipPath: "inset(0 100% 0 0)", filter: "blur(6px)", opacity: 0.4 }}
              animate={{ clipPath: "inset(0 0% 0 0)", filter: "blur(0px)", opacity: 1 }}
              transition={{ duration: 1.8, ease: [0.65, 0, 0.35, 1], delay: 0.15 }}
              className={`w-auto transition-[height,transform] duration-500 group-hover:scale-105 ${scrolled ? "h-10 md:h-11" : "h-12 md:h-14"}`}
            />
            <span aria-hidden="true" style={LOGO_MASK} className="pointer-events-none absolute inset-0 overflow-hidden">
              <motion.span
                initial={{ x: "-120%" }}
                animate={{ x: "220%" }}
                transition={{ duration: 1.6, ease: "easeInOut", delay: 1.7 }}
                className="absolute inset-y-0 left-0 w-1/2 skew-x-[-20deg] bg-gradient-to-r from-transparent via-white/80 to-transparent"
              />
            </span>
          </span>
        </button>
        <nav data-testid="nav-desktop" className="hidden grid-cols-4 items-center gap-x-6 gap-y-2 whitespace-nowrap lg:grid xl:gap-x-10">
          {NAV.slice(1).map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => go(n.id)}
              className="link-underline justify-self-start font-sans text-sm text-sand transition-colors hover:text-cream"
            >
              {n.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nav-whatsapp-cta"
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden items-center gap-2 rounded-full border border-gold/40 px-5 py-2.5 font-sans text-sm text-cream transition-colors hover:bg-gold hover:text-espresso sm:inline-flex"
          >
            <WhatsIcon className="h-4 w-4" /> {T.btn_nav_wa}
          </a>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen((v) => !v)} className="rounded-full border border-gold/30 p-2.5 text-cream lg:hidden" aria-label="Menú">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="mx-4 mt-3 rounded-2xl border border-gold/20 bg-roast/95 p-6 backdrop-blur-xl lg:hidden"
          >
            {NAV.map((n, i) => (
              <motion.button
                key={n.id}
                data-testid={`nav-mobile-link-${n.id}`}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.04 }}
                onClick={() => go(n.id)}
                className="block w-full border-b border-gold/10 py-3 text-left font-display text-2xl text-cream last:border-0"
              >
                {n.label}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
